import React from "react";
import {
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { ANTooltip } from "@answersnow/pkg-theme";
import { TeamMember, TeamRole } from "./interface";
import ActionsMenu from "./ActionsMenu";

interface ViewFieldsProps {
  teamMembers: TeamMember[];
  teamRoles: TeamRole[];
  onOpenMemberPanel: (memberId: string | number, mode?: string) => void;
  canViewMemberDetail: (member: TeamMember) => boolean;
  canEdit?: boolean;
  onRemoveMember?: (row: any) => void;
  onResendInvite?: (row: any) => void;
  onCreateROI?: (row: TeamMember) => void;
  userPermissions?: {
    create_phi_release_request?: boolean;
  };
}

export const ViewFields: React.FC<ViewFieldsProps> = ({
  teamMembers,
  teamRoles,
  onOpenMemberPanel,
  canViewMemberDetail,
  canEdit = false,
  onRemoveMember,
  onResendInvite,
  onCreateROI,
  userPermissions,
}) => {
  const theme = useTheme();

  const getAccountStatus = (member: TeamMember) => {
    const isInviteExpired = member?.expirationDate && new Date(member.expirationDate) < new Date();

    if (!member.hasAcceptedInvite) {
      if (isInviteExpired) {
        return { label: "Record Only", color: "default" as const };
      }

      return { label: "Invite Pending", color: "warning" as const };
    }

    if (!member.userId) {
      return { label: "Record Only", color: "default" as const };
    }

    return { label: "Active", color: "success" as const };
  };

  return (
    <TableContainer
      component={"div"}
      sx={{ borderRadius: "16px", border: `1px solid ${theme.palette.divider}` }}
    >
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <ANTooltip component={TableCell} sx={{ bgcolor: "grey.50", p: 4 }}>
              Member
            </ANTooltip>
            <ANTooltip component={TableCell} sx={{ bgcolor: "grey.50", p: 4 }}>
              Relationship
            </ANTooltip>
            <ANTooltip component={TableCell} sx={{ bgcolor: "grey.50", p: 4 }}>
              Legal Guardian
            </ANTooltip>
            <ANTooltip component={TableCell} sx={{ bgcolor: "grey.50", p: 4 }}>
              Designated Caregiver
            </ANTooltip>
            <ANTooltip component={TableCell} sx={{ bgcolor: "grey.50", p: 4 }}>
              Account Status
            </ANTooltip>
            {canEdit && <TableCell sx={{ bgcolor: "grey.50", width: "120px", p: 4 }}></TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {teamMembers?.map((row) => {
            const accountStatus = getAccountStatus(row);

            return (
              <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell sx={{ p: 4 }}>
                  <Stack>
                    {canViewMemberDetail(row) ? (
                      <ANTooltip
                        component={Link}
                        onClick={() => onOpenMemberPanel(row.id)}
                        underline="hover"
                        sx={{ cursor: "pointer" }}
                      >
                        {row.name}
                      </ANTooltip>
                    ) : (
                      <Typography
                        component="p"
                        variant="body2"
                        noWrap={true}
                        title={row.name || row.email}
                      >
                        {row.name || row.email}
                      </Typography>
                    )}

                    <ANTooltip component={Typography} variant="body2" color="text.secondary">
                      {row.email}
                    </ANTooltip>
                  </Stack>
                </TableCell>
                <ANTooltip component={TableCell} sx={{ p: 4 }}>
                  {teamRoles.find((el) => el.id === row.relationshipType)?.name || "-"}
                </ANTooltip>
                <TableCell sx={{ p: 4 }}>{row.isLegalGuardian ? "Yes" : "No"}</TableCell>
                <TableCell sx={{ p: 4 }}>{row.isDesignatedCaregiver ? "Yes" : "No"}</TableCell>
                <TableCell sx={{ p: 4 }}>
                  <Chip
                    label={accountStatus.label}
                    variant="outlined"
                    size="small"
                    color={accountStatus.color}
                  />
                </TableCell>
                {canEdit && (
                  <ActionsMenu
                    row={row}
                    onRemoveMember={() => onRemoveMember?.(row)}
                    onEditProfile={() => onOpenMemberPanel(row.id, "edit")}
                    onResendInvite={() => onResendInvite?.(row)}
                    onCreateROI={() => onCreateROI?.(row)}
                    userPermissions={userPermissions}
                  />
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ViewFields;
